/**
 * A subset of the global roles in keycloak.
 */
export enum GlobalRole {
  DAS_ADMIN = 'das_admin',
  DEPARTURE_CREATOR = 'departure_creator',
  USER_MANAGEMENT = 'user_management',
  DEVELOPER = 'developer',
  GLOBAL_PROJECT_MANAGER = 'global_project_manager',
  GLOBAL_PROPOSER = 'global_proposer',
  GLOBAL_SSP = 'global_ssp',
  VIEW_ADMIN_PAGE = 'view_admin_page',
  VIEW_ALL_ORGANISATIONS = 'view_all_organisations',
  VIEW_USER_LOCATIONS = 'view_user_locations',
  VIEW_MY_ORGANISATIONS_FILTER = 'view_my_organisations_filter',
  VIEW_MY_DISCIPLINES_FILTER = 'view_my_disciplines_filter',
  VIEW_RESTRICTED_SEARCH_COLUMNS = 'view_restricted_search_columns',
}

export type DasRole = GlobalRole | DepartureRole;

/**
 * An enumeration of the assignable roles on a departure.
 */
export enum DepartureRole {
  DESIGNERS = 'designers',
  PROPOSER = 'proposer',
  PROJECT_MANAGER = 'projectManager',
  SSP = 'ssp',
  TECHNICAL_SPECIALIST = 'technicalSpecialist',
  ADDITIONAL_TECHNICAL_REVIEWERS = 'additionalTechnicalReviewers',
  AUTHORISING_SIGNATORY = 'authorisingSignatory',
  ASSIGNEE = 'assignee',
}

export const DepartureRolesArray: string[] = Object.values(DepartureRole);

/**
 * An enumeration of all the possible authoring actions a user can take against a departure.
 */
export enum AuthoringAction {
  AUTHOR_DEPARTURE_INFORMATION_SECTION = 'AUTHOR_DEPARTURE_INFORMATION_SECTION',
  AUTHOR_PRE_DETERMINATION_ATTACHMENTS = 'AUTHOR_PRE_DETERMINATION_ATTACHMENTS',
  AUTHOR_POST_DETERMINATION_ATTACHMENTS = 'AUTHOR_POST_DETERMINATION_ATTACHMENTS',
  AUTHOR_SUBMISSIONS_SECTION = 'AUTHOR_SUBMISSIONS_SECTION',
  AUTHOR_SPECIALIST_SECTION = 'AUTHOR_SPECIALIST_SECTION',
  CHOOSE_TEMPLATE = 'CHOOSE_TEMPLATE',
  SET_ORGANISATION = 'SET_ORGANISATION',
  AUTHOR_LOCATIONS = 'AUTHOR_LOCATIONS',
  ACCESS_POST_DETERMINATION = 'ACCESS_POST_DETERMINATION',
  EDIT_POST_DETERMINATION = 'EDIT_POST_DETERMINATION',
  AUTHOR_LINKED_DEPARTURES = 'AUTHOR_LINKED_DEPARTURES',
}

/**
 * An subset of the workflow actions actions a user can take against a departure.
 * Special case of non-critical path workflow states are handled separately.
 */
export enum WorkflowAction {
  TRANSITION_CRITICAL_PATH = 'TRANSITION_CRITICAL_PATH',
  REVERT_TO_PREVIOUS_STATE = 'REVERT_TO_PREVIOUS_STATE',
  REWORK_REQUIRED = 'REWORK_REQUIRED',
  WITHDRAW = 'WITHDRAW',
  REJECT = 'REJECT',
  PRE_DETERMINE = 'PRE_DETERMINE',
  ASSIGN_TO_AUTHORISING_SIGNATORY = 'ASSIGN_TO_AUTHORISING_SIGNATORY',
  REVIEW = 'REVIEW',
  DETERMINE = 'DETERMINE',
  CREATE_DEPARTURE_REVISION = 'CREATE_DEPARTURE_REVISION',
  EXTRACT_LOCATIONS = 'EXTRACT_LOCATIONS',
  DAS_ADMIN_REWORK = 'DAS_ADMIN_REWORK',
  PM_APPRAISAL_REWORK = 'PM_APPRAISAL_REWORK',
  SPECIALIST_SUBMISSION_POINT_REWORK = 'SPECIALIST_SUBMISSION_POINT_REWORK',
  PROVISIONAL_AGREEMENT = 'PROVISIONAL_AGREEMENT',
  REQUEST_PROVISIONAL_AGREEMENT = 'REQUEST_PROVISIONAL_AGREEMENT',
}

/**
 * An enumeration of all the possible workflow assignment actions a user can take against a departure.
 */
export enum AssignmentAction {
  ASSIGN = 'ASSIGN',
  ASSIGN_PROPOSER = 'ASSIGN_PROPOSER',
  ASSIGN_DESIGNER = 'ASSIGN_DESIGNER',
  ASSIGN_SSP = 'ASSIGN_SSP',
  ASSIGN_TECHNICAL_SPECIALIST = 'ASSIGN_TECHNICAL_SPECIALIST',
  ASSIGN_ADDITIONAL_TECHNICAL_REVIEWER = 'ASSIGN_ADDITIONAL_TECHNICAL_REVIEWER',
  ASSIGN_AUTHORISING_SIGNATORY = 'ASSIGN_AUTHORISING_SIGNATORY',
  ASSIGN_PM = 'ASSIGN_PM',
  CHANGE_ASSIGNEE = 'CHANGE_ASSIGNEE',
}

/**
 * Lookup from assignment action to the relevant departure role
 */
export const assignmentRoleLookup: Record<AssignmentAction, DepartureRole> = {
  [AssignmentAction.ASSIGN_DESIGNER]: DepartureRole.DESIGNERS,
  [AssignmentAction.ASSIGN_PM]: DepartureRole.PROJECT_MANAGER,
  [AssignmentAction.ASSIGN_PROPOSER]: DepartureRole.PROPOSER,
  [AssignmentAction.ASSIGN_SSP]: DepartureRole.SSP,
  [AssignmentAction.ASSIGN_TECHNICAL_SPECIALIST]: DepartureRole.TECHNICAL_SPECIALIST,
  [AssignmentAction.ASSIGN_ADDITIONAL_TECHNICAL_REVIEWER]: DepartureRole.ADDITIONAL_TECHNICAL_REVIEWERS,
  [AssignmentAction.ASSIGN_AUTHORISING_SIGNATORY]: DepartureRole.AUTHORISING_SIGNATORY,
  [AssignmentAction.CHANGE_ASSIGNEE]: DepartureRole.ASSIGNEE,
  [AssignmentAction.ASSIGN]: void 0,
};

/**
 * An enumeration of all the possible diary actions a user can take against a departure.
 */
export enum DiaryAction {
  COMMENT = 'COMMENT',
}

export enum RecommendationAction {
  MAKE_RECOMMENDATION = 'MAKE_RECOMMENDATION',
}

/**
 * An enumeration of all the possible global actions a user can do.
 */
export enum GlobalAction {
  CREATE_DEPARTURE = 'CREATE_DEPARTURE',
  USER_MANAGEMENT = 'USER_MANAGEMENT',
  VIEW_ADMIN_PAGE = 'VIEW_ADMIN_PAGE',
  VIEW_USER_LOCATIONS = 'VIEW_USER_LOCATIONS',
  LOGOUT = 'LOGOUT',
  VIEW_ALL_ORGANISATIONS = 'VIEW_ALL_ORGANISATIONS',
  VIEW_MY_ORGANISATIONS_FILTER = 'VIEW_MY_ORGANISATIONS_FILTER',
  VIEW_MY_DISCIPLINES_FILTER = 'VIEW_MY_DISCIPLINES_FILTER',
  COMMENT_ON_DEPARTURE = 'COMMENT_ON_DEPARTURE',
  VIEW_RESTRICTED_SEARCH_COLUMNS = 'VIEW_RESTRICTED_SEARCH_COLUMNS',
  VIEW_POST_DETERMINATION_FILTER = 'VIEW_POST_DETERMINATION_FILTER',
}

/**
 * Typedef for all possible DAS 3.0 actions.
 */
export type DasAction =
  | GlobalAction
  | AuthoringAction
  | WorkflowAction
  | AssignmentAction
  | DiaryAction
  | RecommendationAction;

/**
 * Typedef for allowing to pass action enums as a typed paramater to function calls.
 */
export type DasActionType =
  | typeof GlobalAction
  | typeof AuthoringAction
  | typeof WorkflowAction
  | typeof AssignmentAction
  | typeof DiaryAction;
